import { ChevronDown } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import type { CourseTreeNode } from "@/types/generated";
import ChapterNode from "@/components/course/tree/ChapterNode";
import ModuleNumberBadge from "@/components/course/tree/ModuleNumberBadge";
import SectionNode from "@/components/course/tree/SectionNode";
import { useCourseDetailStore } from "@/stores/courseDetailStore";
import { useCourseBasePath } from "@/hooks/useCourseBasePath";
import { extractLeadingNumber } from "@/lib/courseUtils";
import { stripCourseNavQueryString } from "@/lib/chapterNavigation";
import type { AggregatedProgress } from "@/components/course/tree/types";
import {
  treeChromeClass,
  treeTitleClass,
} from "@/components/course/tree/treeStyles";
import { cn } from "@/lib/utils";

interface ModuleNodeProps {
  module: CourseTreeNode;
  moduleProgress?: AggregatedProgress;
  chapterProgress: Map<string, AggregatedProgress>;
  isFirst?: boolean;
}

const ModuleNode = ({
  module,
  moduleProgress,
  chapterProgress,
  isFirst,
}: ModuleNodeProps) => {
  const { expandedModules, toggleModule, selectedNodeId, nodeIndex } =
    useCourseDetailStore();
  const parsedModule = nodeIndex.get(module.id)?.node;
  const hasIntro = Boolean(parsedModule?.intro?.trim());
  const hasOutro = Boolean(parsedModule?.outro?.trim());
  const navigate = useNavigate();
  const location = useLocation();
  const basePath = useCourseBasePath();

  const chapters =
    module.children?.filter(
      (child: CourseTreeNode) => child.type === "chapter"
    ) || [];

  const isExpanded = expandedModules.has(module.id);
  const progress = moduleProgress?.progressPercent ?? 0;
  const isComplete = progress === 100;
  const isSelected = selectedNodeId === module.id;
  const { number, rest } = extractLeadingNumber(module.title);
  const displayTitle = number ? rest : module.title;

  return (
    <div
      id={`node-${module.id}`}
      className={!isFirst ? "border-t border-gray-200" : ""}
    >
      <button
        type="button"
        aria-expanded={isExpanded}
        aria-label={module.title}
        onClick={() => {
          toggleModule(module.id);
          if (selectedNodeId !== module.id) {
            navigate(
              `${basePath}/${module.id}${stripCourseNavQueryString(location.search)}`
            );
          }
        }}
        className="flex w-full items-stretch gap-3 px-5 py-2.5 text-left cursor-pointer transition-colors hover:bg-gray-50"
      >
        <ModuleNumberBadge number={number} complete={isComplete} />
        <div className="flex min-w-0 flex-1 items-center">
          <div
            className={cn(
              "wrap-break-word text-[18px] font-bold leading-snug",
              treeTitleClass(isSelected)
            )}
          >
            {displayTitle}
          </div>
        </div>
        <ChevronDown
          aria-hidden="true"
          className={cn(
            "mt-1 size-4 shrink-0 self-start transition-transform duration-200",
            treeChromeClass,
            isExpanded && "rotate-180"
          )}
        />
      </button>

      {isExpanded && (
        <div className="pb-1">
          {hasIntro && <SectionNode parent={module} kind="intro" />}
          {chapters.map((chapter) => (
            <ChapterNode
              key={chapter.id}
              chapter={chapter}
              chapterProgress={chapterProgress.get(chapter.id)}
            />
          ))}
          {hasOutro && <SectionNode parent={module} kind="outro" />}
        </div>
      )}
    </div>
  );
};

export default ModuleNode;
